import { INBOX_PLATFORMS } from "@content-os/config";
import { cn } from "@/lib/utils";
import { getPlatformLabel } from "@/components/ui/platform-icon";
import { useTranslation } from "react-i18next";

export const INBOX_STATUS_FILTERS = ["OPEN", "SNOOZED", "DONE", "ARCHIVED"] as const;
export type InboxStatusFilter = (typeof INBOX_STATUS_FILTERS)[number];

interface Props {
  status: InboxStatusFilter;
  onStatusChange: (s: InboxStatusFilter) => void;
  /** "" = all channels */
  channel: string;
  onChannelChange: (p: string) => void;
}

/**
 * Shared inbox filter bar: status tabs (Open/Snoozed/Done/Archived) + a channel
 * (platform) dropdown. Used by both the DMs and Comments lists.
 */
export function InboxFilters({ status, onStatusChange, channel, onChannelChange }: Props) {
  const { t } = useTranslation("inbox");
  return (
    <div className="flex-shrink-0 border-b border-border px-2 py-2 space-y-2">
      <div className="flex items-center gap-1">
        {INBOX_STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onStatusChange(s)}
            className={cn(
              "rounded-md px-2 py-1 text-[11px] transition-colors",
              status === s
                ? "bg-accent text-accent-foreground font-medium"
                : "text-muted-foreground hover:bg-surface-2",
            )}
          >
            {t(`status.${s}`)}
          </button>
        ))}
      </div>
      {INBOX_PLATFORMS.length > 1 && (
        <select
          value={channel}
          onChange={(e) => onChannelChange(e.target.value)}
          className="w-full rounded-md border border-border bg-surface-1 px-2 py-1 text-[12px]"
          aria-label={t("filters.channel")}
        >
          <option value="">{t("filters.allChannels")}</option>
          {INBOX_PLATFORMS.map((p) => (
            <option key={p} value={p}>
              {getPlatformLabel(p)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
